import { useRef, useState } from 'react'
import { attachReceipt, correctExpense, removeExpense, type Expense } from '@core/expenses'
import { prepareFile } from '@core/capture'
import { newIdempotencyKey, queueSend } from '@core/documents'
import { categoriesFor, type SpaceKind } from '@core/categories'
import { formatCents, parseAmountToCents } from '@core/money'
import { Button, ErrorNote, Field } from '@design/components'
import { DocumentViewer } from '@features/documents/DocumentViewer'
import { Overlay } from '@design/Overlay'

/**
 * One expense, opened to be put right.
 *
 * This exists because of a trap the two rules made together: an expense is
 * created with a payment already on it, and anything with a payment cannot be
 * thrown away. Each rule is correct alone; together they meant a mistyped
 * amount stayed forever — and kept counting in the total the family sees.
 *
 * Only the admin ever reaches this. A shared space lets the family read what
 * was spent; it does not let them edit it, and that boundary is in Postgres
 * (0033), not in whether this component is rendered.
 */
export function ExpenseSheet({
  expense,
  spaceKind,
  spaceId,
  userId,
  onClose,
  onChanged,
}: {
  expense: Expense
  spaceKind: SpaceKind
  spaceId: string
  userId: string
  onClose: () => void
  onChanged: () => void
}) {
  const [description, setDescription] = useState(expense.description)
  const [amount, setAmount] = useState(centsToField(expense.amountCents))
  const [spentOn, setSpentOn] = useState(expense.spentOn)
  const [category, setCategory] = useState<string | null>(expense.category)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [viewing, setViewing] = useState(false)
  const [added, setAdded] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  async function save() {
    const cents = parseAmountToCents(amount)
    if (cents === null || cents <= 0) {
      setError('Vul een bedrag in.')
      return
    }
    setBusy(true)
    setError('')
    try {
      await correctExpense({
        obligationId: expense.obligationId,
        description,
        amountCents: cents,
        spentOn,
        category,
      })
      onChanged()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Opslaan lukte niet.')
      setBusy(false)
    }
  }

  return (
    <Overlay>
      <input
        ref={fileRef}
        type="file"
        accept="image/*,application/pdf,.pdf"
        capture="environment"
        onChange={async (e) => {
          const file = e.target.files?.[0]
          e.target.value = ''
          if (!file) return
          setError('')
          try {
            const page = await prepareFile(file)
            // Same shape as a new expense: the key names a document that does
            // not exist yet, and 0032's trigger ties them together when it
            // lands. Queued, so no signal simply means later.
            const key = newIdempotencyKey()
            await queueSend({
              spaceId,
              uploadedBy: userId,
              pages: [page.blob],
              source: 'file',
              idempotencyKey: key,
            })
            await attachReceipt(expense.obligationId, key)
            setAdded(true)
            onChanged()
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Die foto lukte niet.')
          }
        }}
        className="hidden"
      />

      <header className="shrink-0 pad-safe-top px-3 pb-2 flex items-center justify-between border-b border-line">
        <button onClick={onClose} className="text-muted px-2 py-2">
          Sluiten
        </button>
        <span className="font-medium">Uitgave</span>
        <span className="w-16" />
      </header>

      <div className="flex-1 scroll-area px-4 py-4">
        <Field
          label="Bedrag"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputMode="decimal"
          autoFocus
        />

        <Field
          label="Waaraan?"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />

        <Field
          label="Wanneer"
          type="date"
          value={spentOn}
          onChange={(e) => setSpentOn(e.target.value)}
        />

        <p className="text-sm text-muted mb-1.5">Soort</p>
        <div className="flex flex-wrap gap-1.5 mb-4">
          {categoriesFor(spaceKind).map((c) => (
            <button
              key={c}
              onClick={() => setCategory(category === c ? null : c)}
              className={`min-h-[2.5rem] px-3 rounded-xl border text-sm ${
                category === c ? 'border-ink bg-ink/5' : 'border-line text-muted'
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* The proof, still reachable. Tapping the row used to open it; now the
            row opens this sheet, so it would otherwise have become unreachable
            for the one person who most needs to check it. */}
        {expense.documentId && (
          <Button tone="secondary" onClick={() => setViewing(true)}>
            Bonnetje bekijken
          </Button>
        )}
        {!expense.documentId && (expense.awaitingReceipt || added) && (
          <p className="text-sm text-muted mb-4">
            Het bonnetje staat nog in de wachtrij. Het verschijnt hier zodra de
            app online is geweest.
          </p>
        )}
        {!expense.documentId && !expense.awaitingReceipt && !added && (
          <>
            <p className="text-sm text-muted mb-2">
              Geen bonnetje. Je kan er alsnog een bij doen.
            </p>
            <Button tone="secondary" onClick={() => fileRef.current?.click()}>
              Bonnetje toevoegen
            </Button>
          </>
        )}

        {/* Said plainly, because a correction that quietly rewrites a ledger is
            worse than one that explains itself. */}
        <p className="text-xs text-muted mb-4 mt-4">
          De oude boeking van {formatCents(expense.amountCents)} blijft in de
          geschiedenis staan, teruggenomen.
        </p>

        <ErrorNote>{error}</ErrorNote>

        <div className="mt-6 pt-4 border-t border-line">
          {!confirming ? (
            <button
              onClick={() => setConfirming(true)}
              className="w-full py-3 text-sm text-muted"
            >
              Weggooien
            </button>
          ) : (
            <div className="text-center">
              <p className="text-sm text-muted mb-2">
                Telt dan niet meer mee. Je kan het 30 dagen terughalen uit de
                prullenbak.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setConfirming(false)}
                  className="flex-1 min-h-[2.75rem] rounded-xl border border-line text-sm"
                >
                  Toch niet
                </button>
                <button
                  disabled={busy}
                  onClick={async () => {
                    setBusy(true)
                    try {
                      await removeExpense(expense.obligationId)
                      onChanged()
                      onClose()
                    } catch (err) {
                      setError(err instanceof Error ? err.message : 'Weggooien lukte niet.')
                      setBusy(false)
                    }
                  }}
                  className="flex-1 min-h-[2.75rem] rounded-xl border border-ink bg-ink/5 text-sm font-medium"
                >
                  Weggooien
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="shrink-0 px-4 pb-4 pt-2 pad-safe-bottom border-t border-line">
        <Button size="large" onClick={save} disabled={busy}>
          {busy ? 'Bezig…' : 'Bewaren'}
        </Button>
      </div>

      {viewing && expense.documentId && (
        <DocumentViewer documentId={expense.documentId} onClose={() => setViewing(false)} />
      )}
    </Overlay>
  )
}

/** Cents back into what someone would have typed: 3500 -> "35,00". */
function centsToField(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',')
}
